import type { SwymAuthor } from '~/features-shared/content/interfaces/swym-content'
import type { IEntity } from '~/features-shared/libs/interfaces/entity'

export enum EProfile {
  Experience = 'experience',
  Education = 'education',
  Preferences = 'preferences',
}

export enum USER_PICTURE_MODE {
  none = '',
  small = 'small',
  compact = 'compact',
  compactx2 = 'compact x2',
  medium = 'medium',
  large = '2xl',
}

/**
 * User state (active, pending, etc...)
 * @info Pending = '0'
 * @info Active = '1'
 * @info LicencseExpired = '2'
 * @info RemovedUser = '3'
 * @info Anonymous = '9'
 */
export enum UserState {
  Pending = '0',
  Active = '1',
  LicenseExpired = '2',
  RemovedUser = '3',
  Anonymous = '9',
}

/**
 * Unique user's id (Same id for all DS services).
 * @info Prefer to use it instead of "login".
 */
export type UserUID = string

/**
 * User's hash key (used as an usid) (only used by 3DSwym services).
 * @info Prefer to use it instead of the "login".
 */
export type UserHashKey = string

export interface UserSkill {
  id: string
  title: string
  description: string
  level: number
  update: string
}
export interface UserSchool {
  crdate: string
  date_begin: string
  date_end: string
  degree: string
  description?: string
  field_of_study: string
  id: string
  not_finished: '0' | '1'
  place: string
  school_name: string
  update: string
}

export interface UserWork {
  crdate: string
  date_begin: string
  date_end: string
  description?: string
  id: string
  industry: string
  not_finished: '0' | '1'
  place: string
  title: string
  update: string
  work_name: string
}

export interface UserInterest {
  crdate: string
  description: string
  id: string
  title: string
  update: string
}

export interface UserLink {
  url: string
  description: string
  id: string
  title: string
  type: string
  update: string
}

export interface UserLanguage {
  description: string
  iso_code: string
  level: number
  id: string
  lang_english: string
  lang_native: string
  language_id: string
  update: string
}

export interface IUser extends Partial<IEntity> {
  /**
   * Unique user's id (Same id for all DS services).
   * @info Prefer to use it instead of "login".
   */
  uid: UserUID

  /**
   * ID in RTConv DB, defined only for RTConv
   */
  rtcid?: string

  /**
   * User's hash key (used as an usid) (only used by 3DSwym services).
   * @info Prefer to use it instead of the "login".
   */
  hash_key?: UserHashKey

  /**
   * User's login.
   * @info Avoid to use it, prefer to use "uid" or "hash_key" instead.
   */
  login: string

  /**
   * Displayable user name (first name + last name).
   * @info Prefer to use it instead of your own concatenation of first and last name.
   */
  displayName: string

  /** User's first name. */
  first_name: string

  /** User's last name. */
  last_name: string

  /**
   * User state (active, pending, etc...).
   * @info Pending = '0'
   * @info Active = '1'
   * @info LicencseExpired = '2'
   * @info RemovedUser = '3'
   * @info Anonymous = '9'
   */
  user_state?: UserState

  /** Same as "user_state" (prefer to use "user_state"). */
  ut_state?: UserState

  /**
   * User picture fingerprint to bypass cache when profile picture is updated
   */
  user_picture_fingerprint?: string

  email?: string
  tel?: string
  picture_filename?: string
  links?: UserLink[]
  zip?: string
  field_of_study?: string
  tenant_more_about_me?: string
  address?: string
  country?: string
  country_code?: string
  city?: string
  company?: string
  skills?: UserSkill[]
  languages?: UserLanguage[]
  interests?: UserInterest[]
  schools?: UserSchool[]
  works?: UserWork[]
  profile_completion?: number
  tenant_visibility?: string
  job_title?: string

  /** Is the user is admin on the current tenant. */
  tenant_admin?: '0' | '1'

  /** Is the user is superuser on the current tenant. */
  tenant_su?: '0' | '1'

  /**
   * Is the user is an Employee or an External user.
   * @info '0': Employee (internal)
   * @info '1': External user
   */
  tenant_user_status?: '0' | '1'

  /** Is the user can create a community. */
  canCreateCommunity?: boolean

  /**
   * Seems to be equal to the login...
   * @info Do not use it until to know its purpose.
   */
  username?: string

  /** Workforce */
  workforce?: {
    nbSkills?: number
    nbLanguages?: number
  }
}

export interface UserInvitation {
  connected_user_to_user: 'none' | 'new'
  user_to_connected_user: 'none' | 'accept' | 'decline'
  user_in_network: 0 | 1
}

export enum UserNetworkStatus {
  NotInNetwork,
  InvitationSentPending,
  InNetwork,
}

export type NetworkUser = Pick<IUser, 'country' |
  'country_code' |
  'first_name' |
  'last_name' |
  'hash_key' |
  'login' |
  'tenant_user_status' |
  'ut_state'> & {
    // TODO check this exist !
    external?: 0 | 1
  }
export type ProfileEducation = Pick<IUser, 'country' |
  'country_code' |
  'first_name' |
  'last_name' |
  'hash_key' |
  'login' |
  'tenant_user_status' |
  'ut_state' >
export interface BackendWikiAuthorizedUser extends SwymAuthor {
  /**
   * User's hash key (used as an usid) (only used by 3DSwym services).
   * @info Prefer to use it instead of the "login".
   */
  hash_key: UserHashKey

  /** Is the user is admin of the wiki community. */
  is_admin: boolean
}

export interface WikiAuthorizedUser extends IUser {
  /**
   * User's hash key (used as an usid) (only used by 3DSwym services).
   * @info Prefer to use it instead of the "login".
   */
  hash_key: UserHashKey

  /** Is the user is admin of the wiki community. */
  is_admin?: boolean
}

/**
 *  User type
 * @info Person
 * @info Group : user group
 */
export enum EUserContentType {
  Person = 'person',
  Group = 'group',
}

/** Options to use the RichUserTooltip. */
export interface RichUserTooltipProps {
  /** To disable the rich user tooltip. */
  disableRichUserTooltip?: boolean
  /** To disable the navigation on user. */
  disableUserLinkNavigation?: boolean
  /** Hash key of the user. */
  userHashKey?: string
  /** Login of the user. */
  userLogin: string
}

/** Options to display a user picture. */
export interface UserPictureProps extends RichUserTooltipProps {
  /**
   * User picture fingerprint.
   * Use it to bypass the cache when the user update his profile picture.
   */
  fingerprint?: string
  /** Should hide the user presence badge? */
  hidePresence?: boolean
  /** User picture mode, default, small or compact. */
  mode?: USER_PICTURE_MODE | string
  /** Name of the user displayed within the simple tooltip. */
  userFullName?: string
}

/** Options to display a username. */
export interface UsernameProps extends RichUserTooltipProps {
  /**
   * The font is bold.
   * @default true
   */
  isBold?: boolean
  /** The name is grey. */
  isGrey?: boolean
  /** The user name is smaller and lighter. */
  smallerAndLighterName?: boolean
  /** Name of the user. */
  userFullName: string
}
